import React, { useState } from "react";
import {
  Box,
  Button,
  CardContent,
  Chip,
  Collapse,
  Link,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MarkdownViewer from "../../utils/MarkdownViewer";
import { formatDate } from "../../Common";
import { useThemeContext } from "../../theme/ThemeContextProvider";
import { danger, neutral, primary, warning } from "../../theme/colors";
import { typography } from "../../theme/typography";
import { QaPayload, Severity } from "../../models/Action";

interface QaFindingCardContentProps {
  expanded: boolean;
  qaPayload: QaPayload;
}

const severityColor = (
  severity: Severity | null | undefined,
  mode: "light" | "dark",
): string => {
  switch (severity) {
    case "blocking":
      return mode === "light" ? danger[500] : danger[400];
    case "warning":
      return mode === "light" ? warning[500] : warning[400];
    case "info":
      return mode === "light" ? primary[500] : primary[400];
    default:
      return mode === "light" ? neutral[500] : neutral[400];
  }
};

const QaFindingCardContent: React.FC<QaFindingCardContentProps> = ({
  expanded,
  qaPayload,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Collapse in={expanded} timeout="auto">
      <CardContent sx={{ p: 3 }}>
        {qaPayload.severity && (
          <Chip
            size="small"
            label={t("DASHBOARD.ACTIONS.QA.SEVERITY." + qaPayload.severity)}
            sx={{
              mb: "12px",
              backgroundColor: severityColor(qaPayload.severity, mode),
              ...typography.bodyXs,
              color: "#FFFFFF",
            }}
          />
        )}

        {qaPayload.subject && (
          <Typography variant="h4" sx={{ display: "block", mb: "8px" }}>
            {qaPayload.subject}
          </Typography>
        )}

        <Box sx={{ mb: "16px" }}>
          <MarkdownViewer markdown={qaPayload.body ?? ""} mode={mode} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: "12px",
            rowGap: "4px",
            mb: "12px",
            ...typography.bodySm,
            color: mode === "light" ? neutral[700] : neutral[200],
          }}
        >
          {qaPayload.code && (
            <>
              <Box>{t("DASHBOARD.ACTIONS.QA.CODE")}</Box>
              <Box>{qaPayload.code}</Box>
            </>
          )}
          {qaPayload.source && (
            <>
              <Box>{t("DASHBOARD.ACTIONS.QA.SOURCE")}</Box>
              <Box>{qaPayload.source}</Box>
            </>
          )}
          {qaPayload.origin_timestamp && (
            <>
              <Box>{t("DASHBOARD.ACTIONS.QA.ORIGIN_TIMESTAMP")}</Box>
              <Box>{formatDate(qaPayload.origin_timestamp)}</Box>
            </>
          )}
        </Box>

        {qaPayload.links && qaPayload.links.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "12px" }}>
            {qaPayload.links.map((link, idx) => (
              <Chip
                key={`qa-link-${idx}`}
                size="small"
                label={
                  <Link
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{ color: "inherit" }}
                  >
                    {link.label || link.url}
                  </Link>
                }
                sx={{ ...typography.bodyXs }}
              />
            ))}
          </Box>
        )}

        {qaPayload.details && Object.keys(qaPayload.details).length > 0 && (
          <Box>
            <Button
              size="small"
              onClick={() => setShowDetails((v) => !v)}
              sx={{
                p: 0,
                ...typography.bodyXs,
                color: primary[500],
                textTransform: "none",
              }}
            >
              {showDetails
                ? t("DASHBOARD.ACTIONS.QA.HIDE_DETAILS")
                : t("DASHBOARD.ACTIONS.QA.SHOW_DETAILS")}
            </Button>
            <Collapse in={showDetails} timeout="auto">
              <Box
                component="pre"
                sx={{
                  mt: "6px",
                  p: "8px",
                  borderRadius: "4px",
                  backgroundColor:
                    mode === "light" ? neutral[50] : neutral[900],
                  overflowX: "auto",
                  ...typography.bodyXs,
                }}
              >
                {JSON.stringify(qaPayload.details, null, 2)}
              </Box>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Collapse>
  );
};

export default QaFindingCardContent;
